import { Box, Chip, Typography } from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Timestamp } from "firebase/firestore";
import { COLORS_BY_STATUS } from "../../utils/colorsStatus";

dayjs.locale("es");

export interface TimelineItem {
  estado: "recibido" | "en_transito" | "listo_para_retirar" | "entregado";
  fecha: string | Timestamp;
  hora: string | Timestamp;
}

interface TimelineProps {
  items: TimelineItem[];
  title?: string;
}

export default function Timeline({ items, title }: TimelineProps) {
  const formatDateTime = (fecha: string | Timestamp, hora: string | Timestamp): string => {
    try {
      let dateObj: dayjs.Dayjs;
      
      if (fecha instanceof Timestamp && hora instanceof Timestamp) {
        // Si son Timestamps, usar la fecha y hora combinadas
        const fechaDate = fecha.toDate();
        const horaDate = hora.toDate();
        const combined = new Date(
          fechaDate.getFullYear(),
          fechaDate.getMonth(),
          fechaDate.getDate(),
          horaDate.getHours(),
          horaDate.getMinutes()
        );
        dateObj = dayjs(combined);
      } else if (typeof fecha === "string" && typeof hora === "string") {
        // Si son strings, combinarlos
        const dateTimeStr = `${fecha} ${hora}`;
        dateObj = dayjs(dateTimeStr);
      } else if (fecha instanceof Timestamp) {
        dateObj = dayjs(fecha.toDate());
      } else {
        dateObj = dayjs(fecha);
      }

      if (!dateObj.isValid()) {
        return "-";
      }

      const dia = dateObj.format("D");
      const mes = dateObj.format("MMMM");
      const horaFormateada = dateObj.format("h:mma").toLowerCase();
      const año = dateObj.format("YYYY");

      return `${dia} de ${mes} de ${año} a las ${horaFormateada}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-";
    }
  };

  const getEstadoLabel = (estado: string): string => {
    return estado.replaceAll("_", " ");
  };

  const getEstadoColor = (estado: string): string => {
    return COLORS_BY_STATUS[estado as keyof typeof COLORS_BY_STATUS]?.[0] || COLORS_BY_STATUS.recibido[0];
  };

  if (!items || items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="text.secondary">No hay historial disponible</Typography>
      </Box>
    );
  }

  // Ordenar items por fecha (más antiguo primero)
  const sortedItems = [...items].sort((a, b) => {
    try {
      const dateA = a.fecha instanceof Timestamp 
        ? a.fecha.toDate() 
        : typeof a.fecha === "string" 
        ? dayjs(a.fecha).toDate() 
        : new Date(0);
      const dateB = b.fecha instanceof Timestamp 
        ? b.fecha.toDate() 
        : typeof b.fecha === "string" 
        ? dayjs(b.fecha).toDate() 
        : new Date(0);
      return dateA.getTime() - dateB.getTime();
    } catch {
      return 0;
    }
  });

  return (
    <Box sx={{ p: 2 }}>
      {title && (
        <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
          {title}
        </Typography>
      )}
      <Box sx={{ position: "relative" }}>
        {/* Línea vertical del timeline */}
        <Box
          sx={{
            position: "absolute",
            left: 20,
            top: 0,
            bottom: 0,
            width: 2,
            backgroundColor: "#e0e0e0",
          }}
        />
        
        {sortedItems.map((item, index) => {
          const fechaHora = formatDateTime(item.fecha, item.hora);
          const estadoLabel = getEstadoLabel(item.estado);
          const estadoColor = getEstadoColor(item.estado);
          const isLast = index === sortedItems.length - 1;
          const uniqueKey = `${item.estado}-${item.fecha}-${item.hora}-${index}`;

          return (
            <Box
              key={uniqueKey}
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "flex-start",
                mb: isLast ? 0 : 3,
                pl: 4,
              }}
            >
              {/* Punto del timeline */}
              <Box
                sx={{
                  position: "absolute",
                  left: 11,
                  top: 4,
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  backgroundColor: estadoColor,
                  border: "3px solid #fff",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  zIndex: 1,
                }}
              />
              
              {/* Contenido */}
              <Box sx={{ flex: 1, ml: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                  <Chip
                    label={estadoLabel}
                    sx={{
                      backgroundColor: estadoColor,
                      color: "#fff",
                      fontWeight: "bold",
                      textTransform: "capitalize",
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {fechaHora}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
